import { useCallback } from 'react';

import { usePathname, useRouter } from 'next/navigation';

import EditorOnboardingModals, {
  type OnboardingModalsProps,
} from '@repo/editor/src/components/OnboardingModal/OnboardingModals';
import { withEventScope } from '@repo/event-processor';

import {
  filterAndSortUseCases,
  getMergedUseCases,
} from 'components/DesignsListPage/v1/utils';
import {
  buildEditorUrlWithTemplateParams,
  OPENED_AI_TOOLBAR_EDITOR_URL,
  FROM_ONBOARDING_QUERY_PARAM,
} from 'global/urlUtils';
import { NO_REDIRECT_URLS_KEYS } from 'global/utils/authRedirectUtils';
import { openInNewTabWithFallback } from 'global/utils/browserUtils';
import { TEMPLATES_PATH } from 'global/utils/templateParams';
import useWindowSize from 'global/utils/useWindowSize';
import { useCasesStore, useCasesStoreSelector } from 'stores/useCasesStore';

import { isNoOnboardingPaths } from './config';

function OnboardingModalsWrapper() {
  const router = useRouter();

  const useCases = useCasesStore(useCasesStoreSelector.useCases);

  const pathname = usePathname();
  const isOnboardingEnabled = !isNoOnboardingPaths(pathname ?? '');

  const { isMobile } = useWindowSize();

  const openUrl = useCallback(
    (url: string) => {
      if (isMobile) {
        window.location.href = url;
      } else {
        openInNewTabWithFallback(url);
      }
    },
    [isMobile]
  );

  const handleFinishSubscriptionFlow = useCallback<
    NonNullable<OnboardingModalsProps['onFinish']>
  >(
    (selection) => {
      if ((pathname && pathname in NO_REDIRECT_URLS_KEYS) || !selection) {
        const defaultUrl = `${TEMPLATES_PATH}?${FROM_ONBOARDING_QUERY_PARAM}=t`;
        void router.push(defaultUrl);
        return;
      }

      switch (selection.type) {
        case 'category':
          openUrl(
            buildEditorUrlWithTemplateParams({
              category: selection.value,
              isNewProject: true,
            })
          );
          break;
        case 'useCase':
          openUrl(
            buildEditorUrlWithTemplateParams({
              useCase: selection.value,
              isNewProject: true,
            })
          );
          break;
        case 'aiImages':
          openUrl(OPENED_AI_TOOLBAR_EDITOR_URL);
          break;
        case 'search':
          openUrl(
            buildEditorUrlWithTemplateParams({
              query: selection.value,
              openTemplateSearchPanel: true,
              isNewProject: true,
            })
          );
          break;
      }
    },
    [pathname, router, openUrl]
  );

  const mergedUseCases = getMergedUseCases(useCases);
  const filteredUseCases = filterAndSortUseCases(mergedUseCases);

  return (
    <EditorOnboardingModals
      enabled={isOnboardingEnabled}
      filteredUseCases={filteredUseCases}
      onFinish={handleFinishSubscriptionFlow}
    />
  );
}

export default withEventScope(OnboardingModalsWrapper, {
  feature: 'onboardingModals',
});
